/**
 * Single source of truth mapping route paths to human-readable screen titles.
 *
 * Used to set {@code document.title} and to announce navigations to screen
 * readers on client-side route changes — single-page apps do neither by default
 * (WCAG 2.4.2 "Page Titled" + SPA route-announcement guidance). Screen titles
 * are short and operator-facing to match the handheld UX.
 *
 * Resolution order: exact path match first, then the longest matching prefix
 * (for parametric routes like {@code /pick/confirm/:id}), then a fallback.
 */
export const APP_NAME = "Eclipse HHU"

/** Fixed routes — exact path → title. */
const EXACT_TITLES: Readonly<Record<string, string>> = {
  "/": "Home",
  "/callback": "Signing in",
  // Receive
  "/receive": "Scan PO",
  "/receive/lines": "Receive Lines",
  "/receive/confirm": "Confirm Receipt",
  "/receive/by-shipment": "Receive by Shipment",
  // Inventory
  "/inventory/by-location": "Inventory by Location",
  "/inventory/by-item": "Inventory by Item",
  "/inventory/adjust": "Adjust Inventory",
  "/inventory/hold": "Hold Inventory",
  "/inventory/count": "Cycle Count",
  "/inventory/change-status": "Change Stock Status",
  // Pick
  "/pick/delivery": "Delivery Pick",
  "/pick/production": "Production Pick",
  "/pick/tickets": "Pick Tickets",
  // Production
  "/production/consume": "Consume Materials",
  "/production/report": "Report Production",
}

/** Parametric routes — path prefix → title (matched longest-first). */
const PREFIX_TITLES: ReadonlyArray<readonly [string, string]> = [
  ["/category/", "Menu"],
  ["/pick/destination/", "Pick Destination"],
  ["/pick/confirm/", "Confirm Pick"],
  ["/pick/complete/", "Pick Complete"],
  ["/pick-batches/", "Pick Batch"],
] as const

/** The fallback title when no route rule matches. */
const FALLBACK_TITLE = "Eclipse HHU"

/**
 * Resolves the human-readable screen title for a route path.
 */
export function resolvePageTitle(pathname: string): string {
  const exact = EXACT_TITLES[pathname]
  if (exact) {
    return exact
  }
  const prefixMatch = PREFIX_TITLES.filter(([prefix]) => pathname.startsWith(prefix)).sort(
    (a, b) => b[0].length - a[0].length,
  )[0]
  return prefixMatch ? prefixMatch[1] : FALLBACK_TITLE
}
